import { useEffect, useRef, useState } from 'react';

import ParticipantsService from '../services/ParticipantsService';
import { useMytaverse } from '../providers/MytaverseProvider';

/**
 * Hook to check if the current user has multiple active game sessions.
 *
 * Returns:
 * - null: Initial state, still checking connections
 * - true: This tab should be blocked because:
 *         - There's a single connection but it's not this tab, or
 *         - There are multiple connections
 * - false: This tab is allowed access (single connection matching this session)
 */
export const useCheckUniqueApplicationAccessPerAccount = () => {
  const { websocketSessionId } = useMytaverse();

  const timerIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;

  // Tracks the connection state (null = loading, true = block, false = allow)
  const [hasMultipleConnections, setHasMultipleConnections] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    /**
     * Logic:
     * 1. If there's exactly one UE5 connection and it matches this session: Allow access
     * 2. If there's exactly one UE5 connection but it's not this session: Block access (with retry for reload)
     * 3. If there are multiple UE5 connections: Block all except the earliest connection
     */
    const checkUniqueAccess = async () => {
      try {
        const wsConnections = await ParticipantsService.getWsConnections();

        // Filter to only get UE5 client connections and sort by joinedToEventAt
        const ue5Connections = wsConnections
          .filter((conn) => {
            /**
             * Only include UE5 connections that have a matching WEBAPP connection with the same sessionId
             * UE5 connection have delay in disconnecting. Active UE5 connection should have a matching WEBAPP connection.
             */
            return (
              conn.clientType === 'UE5' &&
              wsConnections.some(
                (webConn) =>
                  webConn.clientType === 'WEBAPP' &&
                  webConn.sessionId === conn.sessionId,
              )
            );
          })
          .sort((a, b) => (a.joinedToEventAt || 0) - (b.joinedToEventAt || 0));

        // Case 1: Single connection and it's this session - allow access
        if (
          ue5Connections.length === 1 &&
          ue5Connections[0].sessionId === websocketSessionId
        ) {
          setHasMultipleConnections(false);
          return;
        }

        // Case 2: Single connection but not this session - block access
        if (
          ue5Connections.length === 1 &&
          ue5Connections[0].sessionId !== websocketSessionId
        ) {
          /**
           * This is to handle the case where the socket connections is not yet updated to the DB after a reload event page.
           */
          if (retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current++;
            setTimeout(checkUniqueAccess, 1000);
            return;
          }
          // After retries, block access
          setHasMultipleConnections(true);
          return;
        }

        // Case 3: Multiple connections - allow only the earliest connection
        if (ue5Connections.length > 1) {
          const earliestSessionId = ue5Connections[0].sessionId;
          const isEarliestConnection = earliestSessionId === websocketSessionId;
          // If this is not the earliest connection, block it
          setHasMultipleConnections(!isEarliestConnection);
          return;
        }

        setHasMultipleConnections(false);
      } catch (error) {
        console.error('Failed to check connections:', error);
        // On error, default to allowing access
        setHasMultipleConnections(false);
      }
    };

    // Add a 1 second delay for the initial check to allow DB to update
    const initialCheckDelay = setTimeout(() => {
      checkUniqueAccess();
      if (!timerIdRef.current) {
        timerIdRef.current = setInterval(checkUniqueAccess, 30000);
      }
    }, 1000);

    return () => {
      clearTimeout(initialCheckDelay);
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, [websocketSessionId]);

  return hasMultipleConnections;
};
